import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
* A custom element that displays a product price.
* This component listens for variant update events and updates the price display accordingly.
* It handles price updates from two different sources:
* 1. Variant picker (in quick add modal or product page)
* 2. Swatches variant picker (in product cards)
*/

class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    // --- Default Shopify Price Update ---
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // --- Reward Logic Initialization ---
    if (!window._firstProductPriceEl) {
      window._firstProductPriceEl = this;
    }

    if (window._firstProductPriceEl === this) {
      const initialPriceEl = this.querySelector('[ref="priceContainer"]');
      if (initialPriceEl) {
        const initialPrice = initialPriceEl.textContent.trim();
        this.updateRewardPoints(initialPrice);
        console.log('Initial Price:', initialPrice);
      }
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Adds reward-points recalculation at the end.
   * @param {VariantUpdateEvent} event
   */
  updatePrice = (event) => {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    // --- Default variant handling ---
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const newPriceEl = event.detail.data.html.querySelector(
      'product-price [ref="priceContainer"]'
    );
    const currentPriceEl = this.querySelector('[ref="priceContainer"]');

    if (!newPriceEl || !currentPriceEl) return;

    if (currentPriceEl.innerHTML !== newPriceEl.innerHTML) {
      currentPriceEl.replaceWith(newPriceEl);
    }

    // --- Reward update only on first ProductPrice element ---
    if (window._firstProductPriceEl === this) {
      const newPriceText = newPriceEl.textContent.trim();
      this.updateRewardPoints(newPriceText);
      console.log('Variant Changed → New Price:', newPriceText);
    }
  };

  /**
   * Custom function to update reward points based on price.
   */
  updateRewardPoints(priceText) {
    const priceValue = parseFloat(priceText.replace(/[^\d.]/g, ''));
    if (isNaN(priceValue)) return;

    const multiplier = this.getRewardMultiplier();
    const points = Math.round(priceValue * multiplier);
    const rewardStrong = document.querySelector(
      '.Product_besties_rewards_innerboths_infos_new p strong'
    );

    if (rewardStrong) {
      rewardStrong.textContent = `${points} points`;
      console.log('Reward Points Updated →', points);
    }
  }

  getRewardMultiplier() {
    const body = document.body;
    if (!body || !body.classList.contains('is-logged-in')) return 2;

    const rawTags = body.dataset.customerTags || '';
    if (!rawTags) return 2;

    const normalizedTags = rawTags
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
    const normalizedNoSpace = normalizedTags.map((tag) => tag.replace(/\s+/g, '_'));

    if (normalizedNoSpace.includes('swell_vip_bff')) return 4;
    if (normalizedNoSpace.includes('swell_vip_inner_circle')) return 3;

    return 2;
  }
}

// Register the custom element if not already defined
if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
